import React, { useContext } from "react";
import Drawer from "./Drawer";
import { UserContext } from "../contexts/UserContext";
import { NavLink } from "react-router-dom";
import { revokeApplicationPassword } from "../services/users";
import { useAuthenticatedAction } from "../hooks/useAuthenticatedAction";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { ArrowTopRightOnSquareIcon, Cog6ToothIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import RoleBadge from "./RoleBadge";
import Avatar from "./Avatar";
import { baseURL } from "../services/wpConfig";
import "./ProfileDrawer.css";

interface ProfileDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onOpenPreferences: () => void;
}

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({
    isOpen,
    onClose,
    onOpenPreferences,
}) => {
    const { currentUser, setCurrentUser } = useContext(UserContext);
    const { execute: revokePassword } = useAuthenticatedAction();
    const pushNotifications = usePushNotifications();

    const onDisconnect = async () => {
        if (currentUser !== null) {
            try {
                await pushNotifications.unsubscribe();
            } catch {
                /* ignore push unsubscribe errors during logout */
            }
            const result = await revokePassword(revokeApplicationPassword);
            if (result) {
                setCurrentUser(null);
                localStorage.removeItem("user_credentials");
                onClose(); // Close the drawer after disconnection
            }
        }
    };

    if (!currentUser) {
        return null;
    }

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            title="Mon Profil"
        >
            <div className="ProfileDrawer-content">
                <div className="ProfileDrawer-userInfo">
                    <Avatar 
                        url={currentUser.avatar_urls["96"]} 
                        alt={currentUser.name} 
                        size="large"
                    />
                    <h2 className="ProfileDrawer-username">{currentUser.name}</h2>
                    <RoleBadge user={currentUser} />
                </div>
                
                <nav className="ProfileDrawer-menu">
                    <ul>
                        <li>
                            <NavLink 
                                to={`/author/${currentUser.id}`}
                                onClick={onClose}
                                className="ProfileDrawer-menuItem"
                            >
                                <span className="ProfileDrawer-menuItemText">Mes BDs</span>
                            </NavLink>
                        </li>
                        <li>
                            <a
                                href="#"
                                className="ProfileDrawer-menuItem"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onClose();
                                    onOpenPreferences();
                                }}
                            >
                                <Cog6ToothIcon className="ProfileDrawer-leadingIcon" />
                                <span className="ProfileDrawer-menuItemText">Préférences</span>
                                <ChevronRightIcon className="ProfileDrawer-trailingIcon" />
                            </a>
                        </li>
                        <li>
                            <a
                                href={`${baseURL}/wp-admin/profile.php`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ProfileDrawer-menuItem"
                                onClick={onClose}
                            >
                                <span className="ProfileDrawer-menuItemText">Modifier mon profil</span>
                                <ArrowTopRightOnSquareIcon className="ProfileDrawer-trailingIcon" />
                            </a>
                        </li>
                        <li>
                            <a 
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onDisconnect();
                                }}
                                className="ProfileDrawer-menuItem ProfileDrawer-disconnectButton"
                            >
                                <span className="ProfileDrawer-menuItemText">Me déconnecter</span>
                            </a>
                        </li>
                    </ul>
                </nav>
            </div>
        </Drawer>
    );
};

export default ProfileDrawer;
